import * as React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Container, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { useStores } from '../../../../stores';
import { Publisher } from '../../../../domain/Publisher';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AllTerritoryRequestsTable from './AllTerritoryRequestsTable';
import { TerritoryRequest } from '../../../../domain/TerritoryRequest';
import { useConfirm } from 'material-ui-confirm';
import { isMobileOnly } from 'react-device-detect';
import MobileAllTerritoryRequestsTable from './MobileAllTerritoryRequestsTable';
import ShowTerritoryDialog from '../../dialogs/ShowTerritoryDialog';
import { Territory } from '../../../../domain/Territory';
import { useOpenDialog } from '../../../../hooks/useOpenDialog';


const AllTerritoryRequestTab = observer(() => {
    const { t } = useTranslation();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const { publisherStore, territoryStore } = useStores();
    const [publishers, setPublishers] = React.useState<Publisher[]>([]);
    const [selectedTerritory, setSelectedTerritory] = React.useState<Territory | undefined>();
    const confirm = useConfirm();

    React.useEffect(() => {
        publisherStore.getPublishersWithTerritoryRequests((data) => {
            setPublishers(data);
        })
    }, [publisherStore]);

    const assignRequest = (request: TerritoryRequest) => {
        confirm({
            title: t("are_you_sure"),
            description: `${t("do_you_want_to_assign_the_territory")}`,
            confirmationText: t("yes"),
            cancellationText: t("no")
        }).then(() => {
            territoryStore.assignRequestedTerritory(request.id, () => {
                publisherStore.getPublishersWithTerritoryRequests((data) => {
                    setPublishers(data);
                })
            });
        });
    }

    const deleteRequest = (request: TerritoryRequest) => {
        territoryStore.deleteTerritoryRequest(request.id, () => {
            publisherStore.getPublishersWithTerritoryRequests((data) => {
                setPublishers(data);
            })
        });
    }

    const openShowTerritoryDialog = (territory?: Territory) => {
        if (territory) {
            setSelectedTerritory(territory);
            openDialog("show-territory")
        }
    }

    const handleCloseDialog = () => {
        closeDialog();
        setSelectedTerritory(undefined);
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {publishers.map(publ => {
                return <Accordion key={publ.id} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography><b>{`${publ.lastName} ${publ.firstName}`}</b> - {publ.territoryRequests!.length}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        {isMobileOnly ? <MobileAllTerritoryRequestsTable
                            publisher={publ}
                            onTerritoryShow={openShowTerritoryDialog}
                            onRequestAssign={assignRequest}
                            onRequestDelete={deleteRequest}
                        /> : <AllTerritoryRequestsTable
                            publisher={publ}
                            onTerritoryShow={openShowTerritoryDialog}
                            onRequestAssign={assignRequest}
                            onRequestDelete={deleteRequest}
                        />}
                    </AccordionDetails>
                </Accordion>
            })}
            <ShowTerritoryDialog
                territory={selectedTerritory!}
                isOpen={isOpen("show-territory")}
                onClose={handleCloseDialog}
            />
        </Container>
    );
});

export default AllTerritoryRequestTab;